import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, map, mergeMap, take } from 'rxjs';
import { SpecialCharacters, UserRoles } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import {
  DEFAULT_LANGUAGE,
  Language,
} from 'src/app/core/models/languages.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { UserProfilePermissionUtils } from 'src/app/core/utils/permissions/user-profile-permissions/user-profile-permissions.utils';
import { DropdownPosition } from '../../form/components/form-select/form-select.constants';
import { IOption } from '../../form/components/form-select/form-select.model';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
  VALIDATION_MESSAGE,
} from '../../shared-constants';
import {
  ACTIVE_USER_OPTIONS,
  CONSTANTS,
  FORM_STATUS,
  IDS,
  LANGUAGE_DEFAULT,
  LANGUAGES,
  ProfileData,
} from './profile-modal.constants';

@Component({
  selector: 'app-profile-modal',
  templateUrl: './profile-modal.component.html',
  styleUrls: ['./profile-modal.component.scss'],
  standalone: false,
})
export class ProfileModalComponent implements OnInit {
  readonly commonUtils = inject(CommonUtils);
  private locationService = inject(LocationService);
  private destroy = inject(DestroyRef);

  userProfileForm!: FormGroup;
  model!: UserAdmin;
  modalStatus!: FORM_STATUS;
  showButton!: boolean;
  modalHeader!: string;
  currentUser!: UserAdmin;

  locationGroups: IOption[] = [];
  linkedSites: IOption[] = [];
  roleOptions: IOption[] = [];
  disableEmailRole = false;

  readonly LANGS = [
    LANGUAGE_DEFAULT,
    ...LANGUAGES.sort((a, b) =>
      a.label.toString().localeCompare(b.label.toString()),
    ),
  ];
  readonly CONST = CONSTANTS;
  readonly ERROR = VALIDATION_MESSAGE;
  readonly USER_OPTIONS = ACTIVE_USER_OPTIONS;
  readonly IMAGES = IMAGE_SRC;
  readonly CHARS = SpecialCharacters;
  readonly IDS = IDS;
  readonly FORM_STATUS = FORM_STATUS;
  readonly IMAGE_OPTIONS = STATUS_OPTIONS;
  readonly DropdownPosition = DropdownPosition;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: ProfileData,
    private readonly userService: UserService,
    private dialogRef: MatDialogRef<ProfileModalComponent>,
    private fb: FormBuilder,
    private userPermissions: UserProfilePermissionUtils,
  ) {}

  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.currentUser = user;
          this.initializeData();
          this.initializeForm();
          this.getLocationInfo$();
          this.getRoles();
        }),
      )
      .subscribe();

    this.subscribeToEmployeeId();
  }

  onButtonClick() {
    if (this.modalStatus === FORM_STATUS.VIEW_EDIT) {
      this.modalStatus = FORM_STATUS.EDIT;
      this.initializeForm();
    } else {
      this.onSubmit();
    }
  }

  onExit() {
    this.dialogRef.close();
  }

  subscribeToEmployeeId() {
    this.userProfileForm
      .get([IDS.EMPLOYEE_ID])
      ?.valueChanges.pipe(
        distinctUntilChanged((prev, curr) => prev?.trim() === curr?.trim()),
      )
      .subscribe((currentValue: string) => {
        const userIdControl = this.userProfileForm.get(IDS.USER_ID);

        if (!currentValue?.trim()) {
          // If Employee ID is empty, clear User ID
          userIdControl?.reset();
          userIdControl?.updateValueAndValidity();
          return;
        }

        // Otherwise, copy last 10 characters
        const last10 = currentValue.slice(-10);
        userIdControl?.setValue(last10);
        userIdControl?.updateValueAndValidity();
      });
  }

  updateLinkedSites() {
    this.getLinkedSitesInfo$(this.userProfileForm.get(IDS.GROUP)?.value)
      .pipe(take(1))
      .subscribe(() => {
        this.userProfileForm.get(IDS.LOCATION)?.reset();
      });
  }

  initializeForm() {
    if (this.dialogData?.form) {
      this.userProfileForm = this.dialogData.form;
    } else {
      this.userProfileForm = this.fb.group({
        [IDS.EMPLOYEE_ID]: [
          this.model?.employeeId || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.USER_ID]: [
          this.model?.loginId || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.HH_SUPERVISOR]: [
          this.model?.handheldSupervisor
            ? STATUS_OPTIONS.ACTIVE
            : STATUS_OPTIONS.DEACTIVATED,
          Validators.compose([Validators.required]),
        ],
        [IDS.EMAIL]: [
          this.model?.email || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.LAST_NAME]: [
          this.model?.familyName || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.FIRST_NAME]: [
          this.model?.givenName || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.USER_ROLE]: [
          this.model?.roleName || UserRoles.USER,
          Validators.compose([Validators.required]),
        ],
        [IDS.GROUP]: [
          this.model?.locationGroupCode || this.currentUser?.locationGroupCode,
          Validators.compose([Validators.required]),
        ],
        [IDS.LOCATION]: [
          this.model?.locationCode || this.currentUser?.locationCode,
          Validators.compose([Validators.required]),
        ],
        [IDS.ACTIVE]: [
          this.model?.enabled || STATUS_OPTIONS.ACTIVE,
          Validators.compose([Validators.required]),
        ],
      });
    }
    if (this.modalStatus === FORM_STATUS.EDIT) {
      this.userProfileForm.get(IDS.USER_ID)?.disable();
      this.userProfileForm.get(IDS.EMPLOYEE_ID)?.disable();
      this.userProfileForm.get(IDS.EMAIL)?.disable();

      if (this.disableEmailRole) {
        this.userProfileForm.get(IDS.EMAIL)?.disable();
      }
    }

    if (this.modalStatus === FORM_STATUS.ADD) {
      //Add required email validator when adding a User
      this.userProfileForm.get(IDS.EMAIL)?.addValidators(Validators.email);
    }
  }

  getRoles() {
    this.userService
      .getUserRoles$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((roles) => {
          // Add to User Role Dropdown
          roles.forEach((role) => {
            this.roleOptions.push({
              label: role.roleName,
              value: role.roleName,
            });
          });
          this.roleOptions.sort((a, b) =>
            a.label.toString().localeCompare(b.label.toString()),
          );
        }),
      )
      .subscribe();
  }

  isActive(value: any): boolean {
    if (value === STATUS_OPTIONS.ACTIVE) {
      return true;
    }

    return false;
  }

  trim(id: string) {
    const control = this.userProfileForm.get(id);
    control?.setValue(control.value.trim());
  }

  get isButtonDisabled() {
    switch (this.modalStatus) {
      case FORM_STATUS.VIEW:
        return false;
      case FORM_STATUS.ADD:
      case FORM_STATUS.EDIT:
        return this.userProfileForm?.invalid || this.userProfileForm?.pristine;
      default:
        return false;
    }
  }

  get canEditLocationGroup() {
    return this.userPermissions.canEditUserLocationGroup(
      this.currentUser.roleName,
    );
  }

  get canEditLocation() {
    return this.userPermissions.canEditUserLocation(this.currentUser.roleName);
  }

  get canEditUserActive() {
    return !this.dialogData.isOwnProfile;
  }

  get canEditHandheldSupervisor() {
    return !this.dialogData.isOwnProfile;
  }

  get isUserRoleActive() {
    return !this.dialogData.isOwnProfile;
  }

  private onSubmit() {
    const data = this.formatDataForRequest();
    switch (this.modalStatus) {
      case FORM_STATUS.ADD:
        this.userService
          .createUser(data)
          .pipe(take(1))
          .subscribe((status) => {
            this.dialogRef.close(status);
          });
        return;
      case FORM_STATUS.EDIT:
        this.userService
          .editUser(data)
          .pipe(take(1))
          .subscribe((status) => {
            this.dialogRef.close(status);
          });
        return;
    }
  }

  private initializeData() {
    this.model = this.dialogData?.data;
    this.modalStatus = this.dialogData?.formStatus;
    this.disableEmailRole =
      this.dialogData?.disableEmailRole === undefined
        ? false
        : this.dialogData?.disableEmailRole;
    this.showButton =
      this.dialogData?.showButton === undefined
        ? true
        : this.dialogData?.showButton;
    this.modalHeader =
      this.dialogData?.modalHeader === undefined
        ? CONSTANTS.HEADER
        : this.dialogData?.modalHeader;

    //Replace BE values with values that make more sense to user
    if (this.model?.defaultLanguage === DEFAULT_LANGUAGE) {
      this.model.defaultLanguage = this.LANGS[0].label as Language;
    }

    if (this.model?.enabled === true) {
      this.model.enabled = ACTIVE_USER_OPTIONS[0].label as boolean;
    }

    if (this.model?.enabled === false) {
      this.model.enabled = ACTIVE_USER_OPTIONS[1].label as boolean;
    }
  }

  private formatDataForRequest(): UserAdmin {
    let active;
    let isHHsupervisor;
    let group;
    let location;
    const formData = this.userProfileForm.getRawValue();

    active = this.isActive(formData[IDS.ACTIVE]);

    isHHsupervisor = this.isActive(formData[IDS.HH_SUPERVISOR]);

    // need to map group and site codes to ids for request
    group = this.locationGroups.find((group) => group.value === formData.group);
    location = this.linkedSites.find(
      (site) => site.value === formData.location,
    );

    const data: UserAdmin = {
      ...this.model,
      loginId: formData.userId,
      employeeId: formData.employeeId,
      familyName: formData.lastName,
      givenName: formData.firstName,
      email: formData.email,
      enabled: active,
      handheldSupervisor: isHHsupervisor,
      roleName: formData.userRole,
      locationGroupId:
        (group?.optionId as number) ?? this.model.locationGroupId,
      locationGroupName:
        (group?.label as string) ?? this.model.locationGroupName,
      locationId: (location?.optionId as number) ?? this.model.locationId,
      locationName: (group?.label as string) ?? this.model.locationName,
    };
    return data;
  }

  private getLocationInfo$() {
    // get location groups and location sites
    this.locationService
      .getLocationGroups$()
      .pipe(
        take(1),
        mergeMap((data) => {
          const groups = data;
          groups.forEach((group) => {
            this.locationGroups.push({
              label: group.code,
              value: group.code,
              optionId: group.id,
            });
          });
          this.locationGroups.sort((a, b) =>
            a.label.toString().localeCompare(b.label.toString()),
          );

          let groupCode = groups[0]?.code;
          if (this.model?.locationGroupName !== undefined) {
            groupCode = this.model?.locationGroupCode;
          } else if (this.currentUser) {
            groupCode = this.currentUser?.locationGroupCode;
          }

          return this.getLinkedSitesInfo$(groupCode);
        }),
      )
      .subscribe();
  }

  private getLinkedSitesInfo$(groupCode: string) {
    return this.locationService.getLinkedSitesByGroup(groupCode).pipe(
      take(1),
      map((dat) => {
        this.linkedSites = [];
        dat?.response.linkedSitesPerGroup.forEach((site) => {
          this.linkedSites.push({
            label: site.code,
            value: site.code,
            optionId: site.id,
          });
        });
        this.linkedSites.sort((a, b) =>
          a.label.toString().localeCompare(b.label.toString()),
        );
      }),
    );
  }
}
